"use client";

import { useEffect } from "react";
import { playUiTap } from "@/lib/audio/play";

/** Suppresses the global tap for this control subtree (e.g. a custom sound). */
const ATTR_NO_TAP = "data-no-ui-tap-sound";

const DEBOUNCE_MS = 45;
let lastUiTapAt = 0;

function shouldPlayUiTapSound(target: EventTarget | null): boolean {
  if (typeof window === "undefined") return false;
  if (!(target instanceof Element)) return false;

  if (target.closest(`[${ATTR_NO_TAP}]`)) return false;

  if (
    target.closest(
      [
        "button:not([disabled])",
        '[role="button"]:not([aria-disabled="true"])',
        'input[type="submit"]:not([disabled])',
        'input[type="button"]:not([disabled])',
        'input[type="reset"]:not([disabled])',
        'input[type="checkbox"]:not([disabled])',
      ].join(","),
    )
  ) {
    return true;
  }

  const lab = target.closest("label");
  if (lab?.querySelector('input[type="checkbox"]:not([disabled])')) return true;

  const a = target.closest("a[href]");
  if (!(a instanceof HTMLAnchorElement)) return false;
  if (a.getAttribute("aria-disabled") === "true") return false;
  if (a.hasAttribute("download")) return false;

  const href = a.getAttribute("href");
  if (!href || href === "" || href.startsWith("#")) return false;

  if (href.startsWith("/") || href.startsWith("./") || href.startsWith("../")) {
    return true;
  }

  try {
    const u = new URL(href, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Capture-phase click audio for primary controls (buttons, checkboxes, in-app links).
 * Opt out per subtree with `data-no-ui-tap-sound` when a dedicated sound plays instead.
 */
export function UiTapSoundBridge() {
  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      if (!shouldPlayUiTapSound(e.target)) return;
      const now = performance.now();
      if (now - lastUiTapAt < DEBOUNCE_MS) return;
      lastUiTapAt = now;
      try {
        playUiTap();
      } catch {
        /* noop */
      }
    };
    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, []);
  return null;
}
